<script lang="ts">
  import { onMount } from 'svelte'

  import Select from './Select.svelte'
  import {
    BooleanKwarg,
    FloatKwarg,
    IntKwarg,
    KwargError,
    type KwargMap,
    kwargMap,
    kwargNames,
    KwargType,
    ListOfFloatKwarg,
    ReductionTypesKwarg,
  } from '@utils/kwargs.svelte'

  interface Props {
    name: string
    kwargValues:
      | Record<string, number | number[]>
      | Record<string, number>
      | Record<string, number | boolean>
      | Record<string, number | boolean | string>
    kwargTypes: KwargType[]
  }

  let { name, kwargValues = $bindable(), kwargTypes }: Props = $props()

  let kwargs = $state<KwargMap[KwargType][]>([])

  const createEmptyKwarg = () => {
    const defaultType = kwargTypes[0] ?? KwargType.FLOAT
    if (defaultType === KwargType.LIST_OF_FLOAT) {
      return new ListOfFloatKwarg('', [])
    } else if (defaultType === KwargType.INTEGER) {
      return new IntKwarg('', 0)
    } else if (defaultType === KwargType.FLOAT) {
      return new FloatKwarg('', 0)
    } else if (defaultType === KwargType.REDUCTION_TYPES) {
      return new ReductionTypesKwarg(ReductionTypesKwarg.keyword, ReductionTypesKwarg.default)
    } else {
      return new BooleanKwarg('', false)
    }
  }

  onMount(() => {
    kwargs = Object.entries(kwargValues).reduce(
      (acc, [key, value]) => {
        if (kwargTypes.includes(KwargType.LIST_OF_FLOAT) && Array.isArray(value)) {
          acc.push(new ListOfFloatKwarg(key, value))
        } else if (
          kwargTypes.includes(KwargType.INTEGER) &&
          typeof value === 'number' &&
          Number.isInteger(value)
        ) {
          acc.push(new IntKwarg(key, value))
        } else if (kwargTypes.includes(KwargType.FLOAT) && typeof value === 'number') {
          acc.push(new FloatKwarg(key, value))
        } else if (kwargTypes.includes(KwargType.BOOLEAN) && typeof value === 'boolean') {
          acc.push(new BooleanKwarg(key, value))
        } else if (kwargTypes.includes(KwargType.REDUCTION_TYPES) && typeof value === 'string') {
          acc.push(new ReductionTypesKwarg(ReductionTypesKwarg.keyword, value))
        }
        return acc
      },
      [] as KwargMap[KwargType][],
    )
    if (kwargs.length === 0) {
      kwargs = [...kwargs, createEmptyKwarg()]
    }
  })

  const hasEmpty = $derived(kwargs.some((key) => key.name.trim() === ''))

  const addKwarg = () => {
    if (!hasEmpty) {
      kwargs = [...kwargs, createEmptyKwarg()]
    }
  }

  const deleteKwarg = (index: number) => {
    kwargs = kwargs.filter((_, i) => i !== index)
  }

  export const validateKwargs = () => {
    const existingNames: string[] = []
    for (const kwarg of kwargs) {
      if (kwarg.name.trim() === '') {
        throw new KwargError('Keyword name cannot be empty', kwarg)
      }
      if (existingNames.includes(kwarg.name)) {
        throw new KwargError('Duplicate keyword name', kwarg)
      }
      existingNames.push(kwarg.name)
      kwargValues[kwarg.name] = kwarg.value
    }
  }
</script>

<div class="px-4">
  {#each kwargs as kwarg, index (index)}
    <div class="grid gap-2">
      <label class="label" for={`${name}-kwarg-${index}-name`}>Keyword name</label>
      <input
        type="text"
        class="input my-0 box-border flex w-fit px-2 py-1"
        id={`${name}-kwarg-${index}-name`}
        name={`${name}-kwarg-${index}-name`}
        bind:value={kwarg.name}
      />
      <label class="label" for={`${name}-kwarg-${index}-type`}>Keyword type</label>
      <Select
        required
        name={`${name}-kwarg-${index}-type`}
        id={`${name}-kwarg-${index}-type`}
        value={kwarg.type}
        onchange={(e) => {
          const newType = e.currentTarget.value as KwargType
          const newKwargCtor = kwargMap[newType]
          const newKwarg = newKwargCtor.fromOtherKwarg(kwarg)
          kwargs = kwargs.map((k) => (k.name === kwarg.name ? newKwarg : k))
        }}
      >
        {#each Object.keys(kwargMap) as kwargType (kwargType)}
          {#if kwargTypes.includes(kwargType as KwargType)}
            <option value={kwargType}>{kwargNames[kwargType as KwargType]}</option>
          {/if}
        {/each}
      </Select>
      <label class="label" for={`${name}-kwarg-${index}-value`}>Keyword value</label>
      {#if kwarg instanceof FloatKwarg}
        <input
          type="number"
          class="input my-0 box-border flex w-fit px-2 py-1"
          id={`${name}-kwarg-${index}-value`}
          name={`${name}-kwarg-${index}-value`}
          bind:value={kwarg.value}
          step="0.1"
        />
      {:else if kwarg instanceof ListOfFloatKwarg}
        <div class="card variant-outline-surface grid gap-2 p-4">
          {#each kwarg.value as _, index (index)}
            <label class="label" for={`${name}-kwarg-${index}-value`}>Float {index + 1}</label>
            <div class="input-group input-group-divider grid-cols-[1fr_auto]">
              <input
                type="number"
                class="my-0 box-border px-2 py-1"
                id={`${name}-kwarg-${index}-value`}
                name={`${name}-kwarg-${index}-value`}
                bind:value={kwarg.value[index]}
                step="0.1"
              />
              <button
                type="button"
                class="variant-filled-primary btn btn-sm w-min"
                onclick={() => {
                  kwarg.value.splice(index, 1)
                }}>Delete float</button
              >
            </div>
          {/each}
          <button
            type="button"
            class="variant-filled-primary btn btn-sm w-min"
            onclick={() => {
              kwarg.value.push(0)
            }}>Add float</button
          >
        </div>
      {:else if kwarg instanceof IntKwarg}
        <input
          type="number"
          class="input my-0 box-border flex w-fit px-2 py-1"
          id={`${name}-kwarg-${index}-value`}
          name={`${name}-kwarg-${index}-value`}
          bind:value={kwarg.value}
          step="1"
        />
      {:else if kwarg instanceof BooleanKwarg}
        <label
          class="label flex cursor-pointer items-center space-x-2 justify-self-start"
          for={`${name}-kwarg-${index}-value`}
        >
          <input
            type="checkbox"
            class="input my-0 box-border flex w-fit px-2 py-1"
            id={`${name}-kwarg-${index}-value`}
            name={`${name}-kwarg-${index}-value`}
            bind:checked={kwarg.value}
          />
          <span>{kwarg.value ? 'true' : 'false'}</span>
        </label>
      {:else if kwarg instanceof ReductionTypesKwarg}
        <Select
          name={`${name}-kwarg-${index}-value`}
          id={`${name}-kwarg-${index}-value`}
          value={kwarg.value}
          onchange={(e) => {
            kwarg.value = e.currentTarget.value as string
          }}
        >
          <option value="none">none</option>
          <option value="mean">mean</option>
          <option value="sum">sum</option>
        </Select>
      {/if}
      {#if Object.keys(kwargs).length > 1}
        <button
          type="button"
          class="variant-filled-error btn w-min"
          onclick={() => deleteKwarg(index)}
        >
          Remove kwarg {index + 1}
        </button>
      {/if}
    </div>
  {/each}
  <button
    type="button"
    class="variant-filled-primary btn mt-2"
    onclick={addKwarg}
    disabled={hasEmpty}
  >
    Add kwarg
  </button>
</div>
